import React, { useState, useEffect, useCallback } from "react";
import styled from "@emotion/styled";
import { Eta as mmm } from "eta";

import {
  useGetTemplatesQuery,
  useGetTemplateByIdQuery,
  useSavePageMutation,
  Template,
} from "../../api/pagesApi";
import { SavePageModal } from "./SavePageModal";
import { useAppSelector } from "../../hooks/redux";

const Eta: any = mmm; // Import Eta template engine

const BuilderContainer = styled.div`
  display: flex;
  height: 100vh;
  background: #f8f9fa;
  overflow: hidden;
`;

const LeftPanel = styled.div`
  width: 400px;
  background: white;
  border-right: 1px solid #e1e5e9;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
`;

const PanelHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #e1e5e9;
  background: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
`;

const BackButton = styled.button`
  background: transparent;
  border: 1px solid #d1d5db;
  color: #374151;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
  }
`;

const RenderButton = styled.button`
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 107, 107, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SaveButton = styled.button`
  background: linear-gradient(135deg, #38a169 0%, #48bb78 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-left: 8px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(56, 161, 105, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
`;

const PreviewContent = styled.div`
  flex: 1;
  background: white;
  margin: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const PreviewFrame = styled.iframe`
  flex: 1;
  border: none;
  width: 100%;
`;

// Template Selection Components
const CategorySection = styled.div`
  margin-bottom: 32px;
`;

const CategoryTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategoryCount = styled.span`
  background: #f3f4f6;
  color: #6b7280;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
`;

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const TemplateCard = styled.div<{ selected?: boolean }>`
  border: 2px solid ${(props) => (props.selected ? "#FF6B6B" : "#e1e5e9")};
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s;
  background: white;

  &:hover {
    border-color: #ff6b6b;
    box-shadow: 0 4px 12px rgba(255, 107, 107, 0.2);
  }
`;

const TemplateImage = styled.div<{ url?: string }>`
  height: 80px;
  background: ${(props) =>
    props.url
      ? `url(${props.url})`
      : "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)"};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
`;

const TemplateInfo = styled.div`
  padding: 12px;
`;

const TemplateName = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PremiumBadge = styled.span`
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
`;

const TemplateDescription = styled.div`
  color: #6b7280;
  font-size: 12px;
  line-height: 1.4;
`;

// Configuration Components
const ConfigSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
  font-size: 14px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #ff6b6b;
    box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.1);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #ff6b6b;
    box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.1);
  }
`;

const ColorInput = styled.input`
  width: 60px;
  height: 36px;
  padding: 0;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #6b7280;
`;

const ErrorState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #ef4444;
  text-align: center;
`;

const EmptyPreview = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #6b7280;
  text-align: center;

  h3 {
    margin: 0 0 8px 0;
    color: #374151;
  }
`;

const AutoPreviewToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
`;

const ToggleSwitch = styled.input`
  width: 16px;
  height: 16px;
`;

const ToggleLabel = styled.label`
  font-size: 14px;
  color: #374151;
  cursor: pointer;
`;

interface TemplatePageBuilderProps {
  onBack?: () => void;
  onSave?: (data: any) => void;
}
function toNested(flat: Record<string, any>) {
  const out: Record<string, any> = {};
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split(".");
    let cur = out;
    parts.forEach((part, i) => {
      if (i === parts.length - 1) {
        cur[part] = value;
      } else {
        cur = cur[part] = cur[part] ?? {};
      }
    });
  }
  return out;
}
// Initialize Eta template engine
const eta = new Eta({
  tags: ["{{", "}}"], // switch to moustache-style tags
  useWith: true, // lets you write {{colors.primary}}
});

export const TemplatePageBuilder: React.FC<TemplatePageBuilderProps> = ({
  onBack,
  onSave,
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [autoPreview, setAutoPreview] = useState(true); // Auto-preview enabled by default
  const [showConfig, setShowConfig] = useState(false);
  
  // Save modal state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [savedPageUrl, setSavedPageUrl] = useState<string>("");
  
  // Redux state
  const user = useAppSelector((state) => state.auth.user);
  
  // API hooks
  const [savePage, { isLoading: isSaving, error: saveError }] = useSavePageMutation();
  const {
    data: templatesResponse,
    isLoading: isLoadingTemplates,
    error: templatesError,
  } = useGetTemplatesQuery();
  const {
    data: selectedTemplate,
    isLoading: isLoadingTemplate,
    error: templateError,
  } = useGetTemplateByIdQuery(selectedTemplateId, {
    skip: !selectedTemplateId,
  });

  // Extract variables from template using regex
  const extractVarsFromTemplate = useCallback((template: Template) => {
    const extractVars = (tpl: string): string[] => {
      const matches = tpl.match(/{{\s*[=~]?\s*([a-zA-Z0-9_.]+)\s*}}/g);
      if (!matches) return [];
      return matches
        .map((match) => {
          const varMatch = match.match(/{{\s*[=~]?\s*([a-zA-Z0-9_.]+)\s*}}/);
          return varMatch ? varMatch[1] : "";
        })
        .filter((v) => v !== "");
    };

    const allVarsSet = new Set<string>();
    const blockVars: Record<string, string[]> = {};

    if (template.templateBlocks) {
      template.templateBlocks.forEach((block) => {
        // Extract variables from both HTML and CSS content
        const htmlVars = extractVars(block.uiBlock.html || "");
        const cssVars = extractVars(block.uiBlock.css || "");

        // Combine and deduplicate vars for this block
        const uniqueVars = Array.from(new Set([...htmlVars, ...cssVars]));

        blockVars[block.name] = uniqueVars;
        uniqueVars.forEach((v) => allVarsSet.add(v));
      });
    }

    return {
      allVars: Array.from(allVarsSet),
      blockVars,
    };
  }, []);

  // Generate smart default values based on variable names
  const getDefaultValueForVariable = useCallback((varName: string): string => {
    const lowerName = varName.toLowerCase();

    // Color variables
    if (lowerName.includes("color") || lowerName.includes("bg")) {
      return "#FF6B6B";
    }

    // Title/heading variables
    if (lowerName.includes("title") || lowerName.includes("heading")) {
      return "Your Title Here";
    }

    // Description/content variables
    if (
      lowerName.includes("description") ||
      lowerName.includes("content") ||
      lowerName.includes("text")
    ) {
      return "Your description text goes here. Customize this to match your needs.";
    }

    // Company/business variables
    if (lowerName.includes("company") || lowerName.includes("business")) {
      return "Your Company Name";
    }

    // Contact information
    if (lowerName.includes("phone")) return "(555) 123-4567";
    if (lowerName.includes("email")) return "hello@yourcompany.com";
    if (lowerName.includes("address")) return "123 Main St, City, State 12345";

    // CTA/Button text
    if (lowerName.includes("cta") || lowerName.includes("button")) {
      return "Get Started";
    }

    // Default fallback
    return `Edit ${varName}`;
  }, []);

  // Generate smart labels for variables
  const getFieldLabel = useCallback((varName: string): string => {
    // Convert camelCase/snake_case to readable format
    const readable = varName
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .replace(/\./g, " ")
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    return readable;
  }, []);

  // Initialize form data when template is selected
  useEffect(() => {
    if (selectedTemplate) {
      const { allVars } = extractVarsFromTemplate(selectedTemplate);
      const initialData: Record<string, any> = {};

      // Set default values for all variables
      allVars.forEach((varName) => {
        // Try to get default from template
        const defaultValue =
          selectedTemplate.defaultValues?.[varName] ||
          getDefaultValueForVariable(varName);
        initialData[varName] = defaultValue;
      });

      // Also check template blocks for default values
      if (selectedTemplate.templateBlocks) {
        selectedTemplate.templateBlocks.forEach((block) => {
          if (block.defaultValues) {
            Object.assign(initialData, block.defaultValues);
          }
        });
      }

      setFormData(initialData);
      setPreviewHtml("");
      setShowConfig(true);
    }
  }, [selectedTemplate, extractVarsFromTemplate, getDefaultValueForVariable]);

  // Client-side template rendering
  const handleRender = useCallback(() => {
    if (!selectedTemplate) return;

    const templateData = {
      ...toNested(formData), // dotted keys fixed
      colors: {
        ...selectedTemplate.customizationOptions?.colors,
        ...toNested(formData).colors, // user overrides win
      },
      fonts: selectedTemplate.customizationOptions?.fonts || {},
      spacing: selectedTemplate.customizationOptions?.spacing || {},
    };

    let html = "",
      css = "";
    // Render each block in the template

    [...(selectedTemplate.templateBlocks || [])]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .forEach((block) => {
        html += (eta as any).renderString(block.uiBlock.html, templateData);
        css += (eta as any).renderString(block.uiBlock.css, templateData);
      });

    setPreviewHtml(`<!DOCTYPE html><html lang="en"><head><meta charset="utf-8">
    <meta name="viewport" content="width=device-width,initial-scale=1">
    <title>${selectedTemplate.name} Preview</title>
    <style>*{margin:0;padding:0;box-sizing:border-box}
    body{font-family:${templateData.fonts.body || "Arial, sans-serif"};
         line-height:1.6;color:${templateData.colors.text || "#333"}}
    .container{max-width:1200px;margin:0 auto;padding:0 20px}
    ${css}</style></head><body>${html}</body></html>`);
  }, [formData, selectedTemplate]);

  // Save functionality
  const handleSave = useCallback(async (slug: string) => {
    if (!selectedTemplate || !user?.customerId) {
      console.error("Missing template or customer ID");
      return;
    }

    try {
      // Prepare the data to match the API schema
      const templateData = {
        ...toNested(formData),
        colors: {
          ...selectedTemplate.customizationOptions?.colors,
          ...toNested(formData).colors,
        },
        fonts: selectedTemplate.customizationOptions?.fonts || {},
        spacing: selectedTemplate.customizationOptions?.spacing || {},
      };

      // Create blocks array for the API
      const blocks = selectedTemplate.templateBlocks?.map((block, index) => ({
        blockId: block.uiBlock.id,
        configuration: templateData,
        styleOverrides: {},
        isVisible: true,
      })) || [];

      const saveData = {
        title: selectedTemplate.name,
        slug,
        customerId: user.customerId,
        blocks
      };

      console.log("Save page payload:", saveData);

      const result = await savePage(saveData).unwrap();
      
      // Set success state
      setSaveSuccess(true);
      setSavedPageUrl(`${window.location.origin}/pages/${slug}`);
      
      console.log("Page saved successfully:", result);
    } catch (error) {
      console.error("Failed to save page:", error);
    }
  }, [selectedTemplate, formData, user?.customerId, savePage]);

  // Auto-preview with debounce when formData changes
  useEffect(() => {
    if (!autoPreview) return;
    const t = setTimeout(handleRender, 500);
    return () => clearTimeout(t);
  }, [handleRender, autoPreview]);

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setShowConfig(false);
  };

  const handleInputChange = (name: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Group fields by category (enhanced categorization)
  const getFieldCategory = (fieldName: string): string => {
    const lowerName = fieldName.toLowerCase();

    if (lowerName.includes("hero") || lowerName.includes("header")) {
      return "Hero Section";
    }
    if (lowerName.includes("title") || lowerName.includes("heading")) {
      return "Titles & Headings";
    }
    if (lowerName.includes("feature") || lowerName.includes("service")) {
      return "Features";
    }
    if (
      lowerName.includes("contact") ||
      lowerName.includes("phone") ||
      lowerName.includes("address") ||
      lowerName.includes("email") ||
      lowerName.includes("hours")
    ) {
      return "Contact Information";
    }
    if (lowerName.includes("cta") || lowerName.includes("button")) {
      return "Call to Action";
    }
    if (lowerName.includes("color") || lowerName.includes("bg")) {
      return "Colors & Styling";
    }
    if (
      lowerName.includes("company") ||
      lowerName.includes("business") ||
      lowerName.includes("brand")
    ) {
      return "Business Information";
    }
    if (
      lowerName.includes("description") ||
      lowerName.includes("content") ||
      lowerName.includes("text")
    ) {
      return "Content";
    }
    return "General";
  };

  if (isLoadingTemplates) {
    return (
      <BuilderContainer>
        <LoadingState>Loading templates...</LoadingState>
      </BuilderContainer>
    );
  }

  if (templatesError) {
    return (
      <BuilderContainer>
        <ErrorState>Failed to load templates</ErrorState>
      </BuilderContainer>
    );
  }

  // Extract variables and group them by category
  const { allVars, blockVars } = selectedTemplate
    ? extractVarsFromTemplate(selectedTemplate)
    : { allVars: [], blockVars: {} };

  const groupedFields = allVars.reduce((groups, varName) => {
    const category = getFieldCategory(varName);
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push({
      name: varName,
      label: getFieldLabel(varName),
      blockName:
        Object.keys(blockVars).find((blockName) =>
          blockVars[blockName].includes(varName)
        ) || "General",
    });
    return groups;
  }, {} as Record<string, Array<{ name: string; label: string; blockName: string }>>);

  console.log("Grouped Fields:", groupedFields);

  return (
    <BuilderContainer>
      <LeftPanel>
        <PanelHeader>
          <PanelTitle>
            {showConfig ? "Template Configuration" : "Select Template"}
          </PanelTitle>
          {onBack && <BackButton onClick={onBack}>← Back</BackButton>}
        </PanelHeader>

        <ContentArea>
          {!showConfig ? (
            // Template Selection
            <div>
              {templatesResponse && typeof templatesResponse === "object" ? (
                Object.entries(templatesResponse).map(
                  ([categoryName, templates]) => (
                    <CategorySection key={categoryName}>
                      <CategoryTitle>
                        {categoryName.charAt(0).toUpperCase() +
                          categoryName.slice(1)}
                        <CategoryCount>{templates.length}</CategoryCount>
                      </CategoryTitle>
                      <TemplateGrid>
                        {templates.map((template) => (
                          <TemplateCard
                            key={template.id}
                            selected={selectedTemplateId === template.id}
                            onClick={() => handleTemplateSelect(template.id)}
                          >
                            <TemplateImage
                              url={
                                template.thumbnailImage || template.thumbnailUrl
                              }
                            >
                              {!(
                                template.thumbnailImage || template.thumbnailUrl
                              ) && "Preview"}
                            </TemplateImage>
                            <TemplateInfo>
                              <TemplateName>
                                {template.name}
                                {template.isPremium && (
                                  <PremiumBadge>Pro</PremiumBadge>
                                )}
                              </TemplateName>
                              <TemplateDescription>
                                {template.description}
                              </TemplateDescription>
                            </TemplateInfo>
                          </TemplateCard>
                        ))}
                      </TemplateGrid>
                    </CategorySection>
                  )
                )
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    color: "#6b7280",
                    padding: "40px",
                  }}
                >
                  <p>No templates available or unexpected response format.</p>
                </div>
              )}
            </div>
          ) : (
            // Configuration Panel
            <div>
              {isLoadingTemplate ? (
                <LoadingState>Loading template details...</LoadingState>
              ) : templateError ? (
                <ErrorState>Failed to load template details</ErrorState>
              ) : selectedTemplate ? (
                <div>
                  <AutoPreviewToggle>
                    <ToggleSwitch
                      type="checkbox"
                      id="autoPreview"
                      checked={autoPreview}
                      onChange={(e) => setAutoPreview(e.target.checked)}
                    />
                    <ToggleLabel htmlFor="autoPreview">
                      Auto-update preview
                    </ToggleLabel>
                  </AutoPreviewToggle>

                  <p style={{ color: "#6b7280", marginBottom: "24px" }}>
                    {selectedTemplate.description}
                  </p>

                  {Object.entries(groupedFields).map(
                    ([category, categoryFields]) => (
                      <ConfigSection key={category}>
                        <SectionTitle>{category}</SectionTitle>
                        {categoryFields.map((field) => {
                          const fieldName = field.name.toLowerCase();
                          const isLongText =
                            fieldName.includes("description") ||
                            fieldName.includes("content") ||
                            fieldName.includes("text") ||
                            fieldName.includes("subtitle");
                          const isColor =
                            fieldName.includes("color") ||
                            fieldName.includes("bg");
                          const isEmail = fieldName.includes("email");
                          const isPhone = fieldName.includes("phone");

                          return (
                            <FormGroup key={field.name}>
                              <FormLabel>
                                {field.label}
                                <span
                                  style={{
                                    fontSize: "12px",
                                    color: "#6b7280",
                                    fontWeight: "normal",
                                  }}
                                >
                                  {field.blockName !== "General" &&
                                    ` (${field.blockName})`}
                                </span>
                              </FormLabel>
                              {isLongText ? (
                                <FormTextarea
                                  value={formData[field.name] || ""}
                                  onChange={(e) =>
                                    handleInputChange(
                                      field.name,
                                      e.target.value
                                    )
                                  }
                                  placeholder={getDefaultValueForVariable(
                                    field.name
                                  )}
                                />
                              ) : isColor ? (
                                <ColorInput
                                  type="color"
                                  value={formData[field.name] || "#FF6B6B"}
                                  onChange={(e) =>
                                    handleInputChange(
                                      field.name,
                                      e.target.value
                                    )
                                  }
                                />
                              ) : (
                                <FormInput
                                  type={
                                    isEmail ? "email" : isPhone ? "tel" : "text"
                                  }
                                  value={formData[field.name] || ""}
                                  onChange={(e) =>
                                    handleInputChange(
                                      field.name,
                                      e.target.value
                                    )
                                  }
                                  placeholder={getDefaultValueForVariable(
                                    field.name
                                  )}
                                />
                              )}
                            </FormGroup>
                          );
                        })}
                      </ConfigSection>
                    )
                  )}

                  <BackButton
                    onClick={() => setShowConfig(false)}
                    style={{ marginTop: "16px" }}
                  >
                    ← Change Template
                  </BackButton>
                </div>
              ) : null}
            </div>
          )}
        </ContentArea>
      </LeftPanel>

      <RightPanel>
        <PanelHeader>
          <PanelTitle>Live Preview</PanelTitle>
          {showConfig && (
            <ButtonGroup>
              <RenderButton onClick={handleRender} disabled={!selectedTemplate}>
                Render Preview
              </RenderButton>
              <SaveButton 
                onClick={() => setShowSaveModal(true)} 
                disabled={!selectedTemplate || !previewHtml}
              >
                Save Website
              </SaveButton>
            </ButtonGroup>
          )}
        </PanelHeader>

        <PreviewContent>
          {previewHtml ? (
            <PreviewFrame srcDoc={previewHtml} title="Template Preview" />
          ) : (
            <EmptyPreview>
              <h3>No Preview Available</h3>
              <p>
                {!selectedTemplateId
                  ? "Select a template to begin customizing"
                  : !showConfig
                  ? "Template selected! Configure it to see the preview"
                  : 'Click "Render Preview" to see your changes'}
              </p>
            </EmptyPreview>
          )}
        </PreviewContent>
      </RightPanel>
      
      <SavePageModal
        isOpen={showSaveModal}
        onClose={() => {
          setShowSaveModal(false);
          setSaveSuccess(false);
          setSavedPageUrl("");
        }}
        onSave={handleSave}
        isLoading={isSaving}
        error={saveError ? (saveError as any).data?.error || 'Failed to save page' : null}
        success={saveSuccess}
        savedPageUrl={savedPageUrl}
      />
    </BuilderContainer>
  );
};
